import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { calculateEloChange } from '../utils/eloUtils';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { cn } from '../utils/cn';

const GameLog = ({ session, supabase }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [players, setPlayers] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isPwa, setIsPwa] = useState(false);

  // Combined game form state
  const [gameData, setGameData] = useState({
    team1: { player1: '', player2: '', score: 0 },
    team2: { player1: '', player2: '', score: 0 },
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().split(' ')[0].slice(0, 5), // HH:MM format
  });

  // State for player search
  const [searchTerms, setSearchTerms] = useState({
    team1p1: '', team1p2: '', team2p1: '', team2p2: ''
  });
  const [showDropdown, setShowDropdown] = useState({
    team1p1: false, team1p2: false, team2p1: false, team2p2: false
  });
  const searchInputRefs = {
    team1p1: useRef(null), team1p2: useRef(null), team2p1: useRef(null), team2p2: useRef(null)
  };
  const dropdownRefs = {
    team1p1: useRef(null), team1p2: useRef(null), team2p1: useRef(null), team2p2: useRef(null)
  };

  useEffect(() => {
    // Check if running as PWA
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
      setIsPwa(true);
    }

    // Fetch active players
    const fetchPlayers = async () => {
      setLoading(true);
      try {
        const { data: playersData, error: playersError } = await supabase
          .from('players')
          .select('id, username, user_id, elo')
          .eq('is_active', true)
          .order('username');

        if (playersError) throw playersError;

        const allPlayers = (playersData || []).map(player => ({
          ...player,
          source: player.user_id ? 'profile' : 'manual',
          elo: player.elo || 1000,
        })).sort((a, b) => a.username.localeCompare(b.username));

        setPlayers(allPlayers);
      } catch (err) {
        console.error('Error fetching players:', err.message);
        setError('Failed to load players. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchPlayers();
  }, [supabase]);

  // --- Click Outside Handler ---
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(searchInputRefs).forEach(key => {
        // Only close if clicking outside both the input and dropdown
        if (
          searchInputRefs[key].current &&
          !searchInputRefs[key].current.contains(event.target) &&
          dropdownRefs[key].current &&
          !dropdownRefs[key].current.contains(event.target)
        ) {
          // Add a small delay to ensure click events on dropdown items register first
          setTimeout(() => {
            setShowDropdown(prev => ({ ...prev, [key]: false }));
          }, 50);
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    const [field, subField] = name.split('.'); // e.g., "team1.player1" or "date"

    if (subField) {
      // Handle nested state (teams)
      setGameData(prev => ({
        ...prev,
        [field]: {
          ...prev[field],
          [subField]: type === 'number' ? parseInt(value) || 0 : value,
        }
      }));
    } else {
      // Handle top-level state (date, time)
      setGameData(prev => ({
        ...prev,
        [field]: value,
      }));
    }
    // Clear success message on input change
    if (success) setSuccess(false);
     // Clear error message on input change
    if (error) setError(null);
  };
  
  const handleScoreFocus = (e) => {
      const { name } = e.target;
      const [team, field] = name.split('.');
      if (gameData[team][field] === 0) {
          setGameData(prev => ({
              ...prev,
              [team]: { ...prev[team], [field]: '' }
          }));
      }
  };
  
  const handleScoreBlur = (e) => {
      const { name, value } = e.target;
      const [team, field] = name.split('.');
      if (value === '') {
          setGameData(prev => ({
              ...prev,
              [team]: { ...prev[team], [field]: 0 }
          }));
      }
  };

  // Get player object by ID
  const getPlayerById = (id) => players.find(p => p.id === id);

  // Validation
  const validateForm = () => {
    const { team1, team2, date, time } = gameData;
    if (!date || !time) return { valid: false, message: 'Please enter game date and time.' };

    const playerIds = [team1.player1, team1.player2, team2.player1, team2.player2].filter(Boolean);
    if (playerIds.length !== 4) return { valid: false, message: 'Please select 4 players.' };

    const uniquePlayerIds = new Set(playerIds);
    if (uniquePlayerIds.size !== 4) return { valid: false, message: 'Each player must be unique.' };

    if (team1.score < 0 || team2.score < 0) return { valid: false, message: 'Scores cannot be negative.' };
    if (team1.score === team2.score) return { valid: false, message: 'Scores cannot be tied.' }; // Or allow ties if needed
    
    // New validation rule for score ranges
    if ((team1.score > 14 || team2.score > 14) && Math.abs(team1.score - team2.score) > 2) {
      return { valid: false, message: 'When scores exceed 14, they must be within 2 points of each other.' };
    }

    return { valid: true, message: '' };
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null); // Clear previous errors

    const { valid, message } = validateForm();
    if (!valid) {
      setError(message);
      return;
    }

    setSubmitting(true);

    try {
      const playerIds = [
        gameData.team1.player1, gameData.team1.player2,
        gameData.team2.player1, gameData.team2.player2
      ];

      // Fetch current ELOs (consider fetching only if needed or caching)
      const { data: eloData, error: eloError } = await supabase
        .from('players')
        .select('id, elo, game_count')
        .in('id', playerIds);

      if (eloError) throw eloError;

      const currentElos = playerIds.map(id => {
        const player = eloData.find(p => p.id === id);
        return player?.elo || 1000;
      });

      // Calculate ELO changes
      const scores = [gameData.team1.score, gameData.team2.score];
      const nudie = false; // Placeholder for potential Nudie run feature
      const newElos = await calculateEloChange(currentElos, scores, playerIds, nudie);

      // Calculate the ELO changes for each player
      const eloChanges = playerIds.map((id, index) => {
        const player = eloData.find(p => p.id === id);
        const currentElo = player?.elo || 1000;
        return Math.round(newElos[index] - currentElo);
      });

      // Insert game record
      const { data: gameData2, error: gameInsertError } = await supabase
        .from('games')
        .insert([{
          date: gameData.date,
          time: gameData.time,
          team1_player1: gameData.team1.player1,
          team1_player2: gameData.team1.player2,
          team1_score: gameData.team1.score,
          team2_player1: gameData.team2.player1,
          team2_player2: gameData.team2.player2,
          team2_score: gameData.team2.score,
          logged_by: session.user.id,
          // Include player sources if still needed
          team1_player1_source: getPlayerById(gameData.team1.player1)?.source || 'profile',
          team1_player2_source: getPlayerById(gameData.team1.player2)?.source || 'profile',
          team2_player1_source: getPlayerById(gameData.team2.player1)?.source || 'profile',
          team2_player2_source: getPlayerById(gameData.team2.player2)?.source || 'profile',
          // Add ELO change tracking directly in the games table
          team1_player1_elo_before: eloData.find(p => p.id === gameData.team1.player1)?.elo || 1000,
          team1_player1_elo_after: Math.round(newElos[0]),
          team1_player1_elo_change: eloChanges[0],
          
          team1_player2_elo_before: eloData.find(p => p.id === gameData.team1.player2)?.elo || 1000,
          team1_player2_elo_after: Math.round(newElos[1]),
          team1_player2_elo_change: eloChanges[1],
          
          team2_player1_elo_before: eloData.find(p => p.id === gameData.team2.player1)?.elo || 1000,
          team2_player1_elo_after: Math.round(newElos[2]),
          team2_player1_elo_change: eloChanges[2],
          
          team2_player2_elo_before: eloData.find(p => p.id === gameData.team2.player2)?.elo || 1000,
          team2_player2_elo_after: Math.round(newElos[3]),
          team2_player2_elo_change: eloChanges[3],
        }])
        .select('id');

      if (gameInsertError) throw gameInsertError;

      // Update player ELOs and game counts
      const updatePromises = playerIds.map((id, index) => {
        const player = eloData.find(p => p.id === id);
        const currentGameCount = player?.game_count || 0;
        
        return supabase
          .from('players')
          .update({ 
            elo: Math.round(newElos[index]),
            game_count: currentGameCount + 1 
          })
          .eq('id', id);
      });

      const updateResults = await Promise.allSettled(updatePromises);
      updateResults.forEach((result, index) => {
          if (result.status === 'rejected') {
              console.error(`Error updating player ${playerIds[index]}:`, result.reason?.message);
              // Optionally collect these errors to show a partial success message
          }
      });


      setSuccess(true);
      // Reset form
      setGameData({
        team1: { player1: '', player2: '', score: 0 },
        team2: { player1: '', player2: '', score: 0 },
        date: new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().split(' ')[0].slice(0, 5),
      });

      // Redirect after a short delay
      setTimeout(() => {
        navigate('/');
      }, 1500);

    } catch (err) {
      console.error('Error logging game:', err.message);
      setError(`Failed to log game: ${err.message}`);
      setSuccess(false); // Ensure success is false if error occurs
    } finally {
      setSubmitting(false);
    }
  };

  // Handle player search input change
  const handleSearchChange = (key, value) => {
    setSearchTerms(prev => ({ ...prev, [key]: value }));
    
    // Show dropdown when typing
    setShowDropdown(prev => ({ ...prev, [key]: true }));
    
    // Clear the player selection if search box is cleared
    if (value === '') {
      try {
        // Extract team and player info without regex
        const parts = key.split('p');
        const team = parts[0]; // "team1"
        const playerNumStr = parts[1]; // "1"
        const playerField = `player${playerNumStr}`; // "player1"
        
        if (gameData[team]) {
          setGameData(prev => ({
            ...prev,
            [team]: {
              ...prev[team],
              [playerField]: ''
            }
          }));
        }
      } catch (err) {
        console.error('Error in handleSearchChange:', err, 'key:', key);
      }
    }
  };

  // Handle selecting a player from the dropdown
  const handlePlayerSelect = (key, playerId) => {
    try {
      // Extract team and player info without regex
      // For "team1p1", split at "p" gives ["team1", "1"]
      const parts = key.split('p');
      const team = parts[0]; // "team1"
      const playerNumStr = parts[1]; // "1"
      const playerField = `player${playerNumStr}`; // "player1"
      
      // Find the selected player to display their name in the search field
      const selectedPlayer = players.find(p => p.id === playerId);
      
      // Update the game data with the selected player ID
      if (gameData[team]) {
        setGameData(prev => ({
          ...prev,
          [team]: {
            ...prev[team],
            [playerField]: playerId
          }
        }));
      } else {
        console.error(`Team ${team} not found in gameData`, gameData);
      }
      
      // Update the search term with the player's name for better UX
      if (selectedPlayer) {
        setSearchTerms(prev => ({ 
          ...prev, 
          [key]: selectedPlayer.username 
        }));
      }
      
      // Hide dropdown after selection
      setShowDropdown(prev => ({ ...prev, [key]: false }));
    } catch (err) {
      console.error('Error in handlePlayerSelect:', err, 'key:', key, 'playerId:', playerId);
    }
  };

  // Handle focus on search input
  const handleSearchFocus = (key) => {
    setShowDropdown(prev => ({ ...prev, [key]: true }));
  };

  // Render filtered player options for a specific search input
  const renderFilteredPlayerOptions = (key, excludeIds = []) => {
    const searchTerm = searchTerms[key].toLowerCase();
    
    try {
      // Extract team and player info without regex
      const parts = key.split('p');
      const team = parts[0]; // "team1"
      const playerNumStr = parts[1]; // "1"
      const playerField = `player${playerNumStr}`; // "player1"
      
      // Add safety check to prevent undefined access
      const selectedPlayerId = gameData[team] ? gameData[team][playerField] : '';
      
      // Get all currently selected player IDs
      const selectedPlayerIds = [
        gameData.team1.player1, 
        gameData.team1.player2, 
        gameData.team2.player1, 
        gameData.team2.player2
      ].filter(Boolean);
      
      // Filter out already selected players
      const filteredPlayers = players.filter(p =>
        !excludeIds.includes(p.id) &&
        p.username.toLowerCase().includes(searchTerm)
      );

      // Check if any players are excluded from the search results
      const excludedPlayers = players.filter(p => 
        p.username.toLowerCase().includes(searchTerm) && 
        excludeIds.includes(p.id)
      );

      if (filteredPlayers.length === 0 && searchTerm) {
          return (
            <>
              <li className="no-results p-2 text-sm text-muted-foreground">No players found</li>
              {excludedPlayers.length > 0 && (
                <li className="p-2 text-sm text-amber-600">
                  {excludedPlayers.length === 1 
                    ? "1 player is already selected elsewhere" 
                    : `${excludedPlayers.length} players are already selected elsewhere`}
                </li>
              )}
            </>
          );
      }
      
      if (filteredPlayers.length === 0 && !searchTerm) {
           return <li className="no-results p-2 text-sm text-muted-foreground">Type to search...</li>;
      }

      return (
        <>
          {filteredPlayers.map(player => (
            <li
              key={player.id}
              onClick={() => handlePlayerSelect(key, player.id)}
              className={cn(
                "player-option p-2 text-sm hover:bg-primary/10 cursor-pointer transition-colors",
                player.id === selectedPlayerId && "bg-primary/20 font-medium"
              )}
            >
              {player.username} (ELO: {Math.round(player.elo || 1000)})
            </li>
          ))}
          {excludedPlayers.length > 0 && searchTerm && (
            <li className="p-2 text-sm text-amber-600 border-t border-border">
              {excludedPlayers.length === 1 
                ? "1 matching player is already selected elsewhere" 
                : `${excludedPlayers.length} matching players are already selected elsewhere`}
            </li>
          )}
        </>
      );
    } catch (err) {
      console.error('Error in renderFilteredPlayerOptions:', err, 'key:', key);
      return <li className="no-results p-2 text-sm text-muted-foreground">Error loading players</li>;
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading players...</p>
      </div>
    );
  }
  
  const selectedIds = [
    gameData.team1.player1,
    gameData.team1.player2,
    gameData.team2.player1,
    gameData.team2.player2
  ].filter(Boolean); // Filter out empty strings

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <Card className="shadow-md">
        <CardHeader className="border-b bg-muted/50">
          <CardTitle className="text-2xl font-bold text-center">Log a Game</CardTitle>
        </CardHeader>
        <CardContent className="pt-6 pb-2">
          {error && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive text-destructive rounded-md">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-md">
              Game logged successfully!
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Game Date and Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="date" className="block text-sm font-medium">
                  Date
                </label>
                <input
                  type="date"
                  name="date"
                  id="date"
                  value={gameData.date}
                  onChange={handleInputChange}
                  className="w-full p-2 rounded-md border border-border bg-background"
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="time" className="block text-sm font-medium">
                  Time
                </label>
                <input
                  type="time"
                  name="time"
                  id="time"
                  value={gameData.time}
                  onChange={handleInputChange}
                  className="w-full p-2 rounded-md border border-border bg-background"
                  required
                />
              </div>
            </div>
            
            {/* Teams Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Team 1 */}
              <div className="space-y-4 p-4 border border-border rounded-lg bg-muted/30">
                <h3 className="font-medium text-lg text-center">Team 1</h3>
                
                {/* Player 1 */}
                <div className="space-y-2 relative">
                  <label htmlFor="team1.player1" className="block text-sm font-medium">
                    Player 1
                  </label>
                  <input
                    type="text"
                    ref={searchInputRefs.team1p1}
                    placeholder="Search for player..."
                    value={searchTerms.team1p1}
                    onChange={(e) => handleSearchChange('team1p1', e.target.value)}
                    onFocus={() => handleSearchFocus('team1p1')}
                    className="w-full p-2 rounded-md border border-border bg-background"
                  />
                  {showDropdown.team1p1 && (
                    <div 
                      ref={dropdownRefs.team1p1}
                      className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-md bg-background border border-border shadow-lg"
                    >
                      <ul className="py-1">
                        {renderFilteredPlayerOptions('team1p1', [
                          gameData.team1.player2, 
                          gameData.team2.player1, 
                          gameData.team2.player2
                        ])}
                      </ul>
                    </div>
                  )}
                </div>
                
                {/* Player 2 */}
                <div className="space-y-2 relative">
                  <label htmlFor="team1.player2" className="block text-sm font-medium">
                    Player 2
                  </label>
                  <input
                    type="text"
                    ref={searchInputRefs.team1p2}
                    placeholder="Search for player..."
                    value={searchTerms.team1p2}
                    onChange={(e) => handleSearchChange('team1p2', e.target.value)}
                    onFocus={() => handleSearchFocus('team1p2')}
                    className="w-full p-2 rounded-md border border-border bg-background"
                  />
                  {showDropdown.team1p2 && (
                    <div 
                      ref={dropdownRefs.team1p2}
                      className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-md bg-background border border-border shadow-lg"
                    >
                      <ul className="py-1">
                        {renderFilteredPlayerOptions('team1p2', [
                          gameData.team1.player1, 
                          gameData.team2.player1, 
                          gameData.team2.player2
                        ])}
                      </ul>
                    </div>
                  )}
                </div>
                
                {/* Score */}
                <div className="space-y-2">
                  <label htmlFor="team1.score" className="block text-sm font-medium">
                    Score
                  </label>
                  <input
                    type="number"
                    name="team1.score"
                    min="0"
                    value={gameData.team1.score}
                    onChange={handleInputChange}
                    onFocus={handleScoreFocus}
                    onBlur={handleScoreBlur}
                    className="w-full p-2 rounded-md border border-border bg-background"
                    required
                  />
                </div>
              </div>
              
              {/* Team 2 */}
              <div className="space-y-4 p-4 border border-border rounded-lg bg-muted/30">
                <h3 className="font-medium text-lg text-center">Team 2</h3>
                
                {/* Player 1 */}
                <div className="space-y-2 relative">
                  <label htmlFor="team2.player1" className="block text-sm font-medium">
                    Player 1
                  </label>
                  <input
                    type="text"
                    ref={searchInputRefs.team2p1}
                    placeholder="Search for player..."
                    value={searchTerms.team2p1}
                    onChange={(e) => handleSearchChange('team2p1', e.target.value)}
                    onFocus={() => handleSearchFocus('team2p1')}
                    className="w-full p-2 rounded-md border border-border bg-background"
                  />
                  {showDropdown.team2p1 && (
                    <div 
                      ref={dropdownRefs.team2p1}
                      className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-md bg-background border border-border shadow-lg"
                    >
                      <ul className="py-1">
                        {renderFilteredPlayerOptions('team2p1', [
                          gameData.team1.player1, 
                          gameData.team1.player2, 
                          gameData.team2.player2
                        ])}
                      </ul>
                    </div>
                  )}
                </div>
                
                {/* Player 2 */}
                <div className="space-y-2 relative">
                  <label htmlFor="team2.player2" className="block text-sm font-medium">
                    Player 2
                  </label>
                  <input
                    type="text"
                    ref={searchInputRefs.team2p2}
                    placeholder="Search for player..."
                    value={searchTerms.team2p2}
                    onChange={(e) => handleSearchChange('team2p2', e.target.value)}
                    onFocus={() => handleSearchFocus('team2p2')}
                    className="w-full p-2 rounded-md border border-border bg-background"
                  />
                  {showDropdown.team2p2 && (
                    <div 
                      ref={dropdownRefs.team2p2}
                      className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-md bg-background border border-border shadow-lg"
                    >
                      <ul className="py-1">
                        {renderFilteredPlayerOptions('team2p2', [
                          gameData.team1.player1, 
                          gameData.team1.player2, 
                          gameData.team2.player1
                        ])}
                      </ul>
                    </div>
                  )}
                </div>
                
                {/* Score */}
                <div className="space-y-2">
                  <label htmlFor="team2.score" className="block text-sm font-medium">
                    Score
                  </label>
                  <input
                    type="number"
                    name="team2.score"
                    min="0"
                    value={gameData.team2.score}
                    onChange={handleInputChange}
                    onFocus={handleScoreFocus}
                    onBlur={handleScoreBlur}
                    className="w-full p-2 rounded-md border border-border bg-background"
                    required
                  />
                </div>
              </div>
            </div>
            
            <div className="pt-4 pb-2">
              <Button
                type="submit"
                className="w-full"
                disabled={submitting}
                onClick={handleSubmit}
              >
                {submitting ? 'Logging Game...' : 'Log Game'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      
      {/* Helper Text */}
      {!isPwa && (
        <div className="mt-6 text-center text-sm text-muted-foreground">
          <p>Pro tip: Install this app on your device for a better experience!</p>
        </div>
      )}
    </div>
  );
};

export default GameLog;